/* eslint-disable i18next/no-literal-string */
// src/components/errors/ErrorQuickActions.jsx
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Api as ApiIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { tokens } from '../../../theme';

export default function ErrorQuickActions({
  summary,
  onNavigateToBrowser,
  onNavigateToEndpoint,
  onNavigateToErrorCode,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (!summary) return null;

  // Get top 5 endpoints and error codes
  const topEndpoints = Object.entries(summary.errors_by_endpoint || {})
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5);

  const topErrorCodes = Object.entries(summary.errors_by_code || {})
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5);

  return (
    <Grid container spacing={3} sx={{ mt: 2 }}>
      {/* Quick Browse Actions */}
      <Grid item xs={12} md={4}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <SearchIcon color="primary" />
              <Typography variant="h6">Quick Actions</Typography>
            </Box>
            
            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={onNavigateToBrowser}
                fullWidth
              >
                Browse All Errors
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<TrendingUpIcon />}
                onClick={() => onNavigateToEndpoint(topEndpoints[0]?.[0])}
                disabled={!topEndpoints[0]}
                fullWidth
              >
                View Top Problem Endpoint
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<ErrorIcon />}
                onClick={() => onNavigateToErrorCode(topErrorCodes[0]?.[0])}
                disabled={!topErrorCodes[0]}
                fullWidth
              >
                View Most Frequent Error
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Top Error-Prone Endpoints */}
      <Grid item xs={12} md={4}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justify-content="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <ApiIcon color="warning" />
                <Typography variant="h6">Top Endpoints</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                By error count
              </Typography>
            </Box>
            
            <List dense>
              {topEndpoints.map(([endpoint, count], index) => {
                // Extract method and path
                const parts = endpoint.split(' ');
                const method = parts[0] || '';
                const path = parts.slice(1).join(' ') || endpoint;
                
                return (
                  <ListItem key={endpoint} disablePadding>
                    <ListItemButton
                      onClick={() => onNavigateToEndpoint(endpoint)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': {
                          backgroundColor: colors.primary[900],
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip 
                              label={method} 
                              size="small" 
                              color="primary"
                              sx={{ 
                                fontFamily: 'monospace', 
                                fontSize: '0.7rem',
                                minWidth: 45,
                              }}
                            />
                            <Typography 
                              variant="body2" 
                              fontFamily="monospace"
                              sx={{ 
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={path}
                            >
                              {path.length > 25 ? `...${path.slice(-22)}` : path}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="caption" color="error.main">
                              {count} errors
                            </Typography>
                            <ArrowForwardIcon sx={{ fontSize: 14, color: 'action.disabled' }} />
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
            
            {topEndpoints.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                No endpoint data available
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Top Error Codes */}
      <Grid item xs={12} md={4}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justify-content="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <ErrorIcon color="error" />
                <Typography variant="h6">Top Error Codes</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                By frequency
              </Typography>
            </Box>
            
            <List dense>
              {topErrorCodes.map(([errorCode, count], index) => (
                <ListItem key={errorCode} disablePadding>
                  <ListItemButton
                    onClick={() => onNavigateToErrorCode(errorCode)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&:hover': {
                        backgroundColor: colors.redAccent[900],
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography 
                          variant="body2" 
                          fontFamily="monospace"
                          fontWeight="medium"
                          color="error.main"
                        >
                          {errorCode}
                        </Typography>
                      }
                      secondary={
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">
                            {count} occurrences ({((count / summary.total_errors) * 100).toFixed(1)}%)
                          </Typography>
                          <ArrowForwardIcon sx={{ fontSize: 14, color: 'action.disabled' }} />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            
            {topErrorCodes.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                No error code data available
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}